import { Component, input } from '@angular/core';
import { MatCard, MatCardContent, MatCardTitle } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { TranslocoDirective } from '@jsverse/transloco';
import { DateFormatPipe } from '@processpuzzle/base-entity';
import { Invoice } from '@processpuzzle/platform-admin';

/**
 * The customer's own invoices, newest period first.
 *
 * **Totals are rendered as `invoice.total`** — the formatted string off the model — never as
 * `totalMinor`. That integer is the currency's minor unit, and `formatMinor` in
 * `@processpuzzle/platform-admin` is the one place in the platform that divides it. A second
 * division here would be a second chance to get wrong a figure the customer has already been
 * charged.
 *
 * **A `DRAFT` has no number, and the table says so rather than showing a blank cell.** Invoice
 * numbers must be gapless, so one is assigned at issue rather than at creation; an empty cell would
 * read as data missing instead of as an invoice not yet issued.
 *
 * Unpaged, matching the contract: a trial customer has no invoices and an established one has one
 * per period, so paging a list that grows twelve rows a year would be machinery for nothing.
 *
 * There is no download link. Nothing in this platform renders an invoice PDF yet, and a button that
 * produced a 404 would be worse than its absence.
 */
@Component({
  selector: 'pp-invoice-list',
  imports: [MatCard, MatCardTitle, MatCardContent, MatTableModule, TranslocoDirective, DateFormatPipe],
  template: `
    <ng-container *transloco="let t; prefix: 'platform_subscription.portal.invoices'">
      <mat-card class="invoice-card" test-id="portal-invoice-list">
        <mat-card-title>{{ t('title') }}</mat-card-title>
        <mat-card-content>
          @if (invoices().length === 0) {
            <p test-id="portal-invoices-empty">{{ t('empty') }}</p>
          } @else {
            <table mat-table [dataSource]="invoices()" class="invoice-table">
              <ng-container matColumnDef="number">
                <th mat-header-cell *matHeaderCellDef>{{ t('number') }}</th>
                <td mat-cell *matCellDef="let invoice">{{ invoice.number ?? t('draft') }}</td>
              </ng-container>

              <ng-container matColumnDef="period">
                <th mat-header-cell *matHeaderCellDef>{{ t('period') }}</th>
                <td mat-cell *matCellDef="let invoice">{{ invoice.periodStart | ppDate }} – {{ invoice.periodEnd | ppDate }}</td>
              </ng-container>

              <ng-container matColumnDef="status">
                <th mat-header-cell *matHeaderCellDef>{{ t('status') }}</th>
                <td mat-cell *matCellDef="let invoice">{{ t('statuses.' + invoice.status) }}</td>
              </ng-container>

              <ng-container matColumnDef="total">
                <th mat-header-cell *matHeaderCellDef>{{ t('total') }}</th>
                <td mat-cell *matCellDef="let invoice" class="invoice-total">{{ invoice.total }}</td>
              </ng-container>

              <tr mat-header-row *matHeaderRowDef="columns"></tr>
              <tr mat-row *matRowDef="let row; columns: columns" [attr.test-id]="'portal-invoice-' + row.id"></tr>
            </table>
          }
        </mat-card-content>
      </mat-card>
    </ng-container>
  `,
  styles: [
    `
      .invoice-card {
        max-width: 720px;
      }

      .invoice-table {
        width: 100%;
      }

      .invoice-total {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
    `,
  ],
})
export class InvoiceListComponent {
  readonly invoices = input<Invoice[]>([]);

  readonly columns = ['number', 'period', 'status', 'total'];
}
